import React from 'react';
import { reduce } from 'lodash';
import { connect } from 'react-redux';
import GridView from '../../../views/gridView/GridView';
import ModelCard from './ModelCard';
import { get, some, filter } from 'lodash';
import { MODELS } from '../../../common/steps';
import ConfigPage from '../ConfigPage';
import { buildCgiImageForCard } from '../../../api/config/normalizeCGI';
import { findClosestMsrp } from '../findClosestMsrp';
import { normalizeEvFeature } from '../../../resources/normalizeHelpers/normalizeHelpers.constant';
import AlignCardsContent from '../../../common/AlignCardsContent';

const NormalizeBodyTypeOptions = ({ matrix, cgiSizes, children }) => {
  const { bodyTypes } = matrix;

  const normalizedOptions = bodyTypes.map((bodyType) => {
    const {
      cabSize,
      description,
      defaultTrim,
      id,
      shortCfd,
      evFeatures,
      disclaimers,
      subHeader,
    } = bodyType;
    const cgiImageUrl = buildCgiImageForCard(defaultTrim.cgiImageUrl, cgiSizes);

    const normalizedDisclaimers = reduce(
      disclaimers,
      (hash, disclosure) => {
        hash[disclosure.ss] = disclosure.label;
        return hash;
      },
      {}
    );
    const normalizedShortCfd = normalizeEvFeature(
      shortCfd,
      normalizedDisclaimers
    );

    const { msrp, msrpAsShown } = defaultTrim;
    return {
      bodyType: id,
      shortCfd: normalizedShortCfd,
      disclaimers: normalizedDisclaimers,
      description,
      cabSize,
      cgiImageUrl,
      featuresLabel: '',
      featureDisclaimers: evFeatures?.featureDisclaimers,
      formattedFeatures: evFeatures?.featuresFormatted,
      featureTitle: evFeatures?.featureTitle,
      features: evFeatures?.features,
      additionalOptionsLabel:
        evFeatures?.optionsFormatted === null
          ? 'Additional Options'
          : evFeatures?.optionsFormatted,
      moreFeatureDisclaimers: evFeatures?.addFeatureDisclaimers,
      moreFeatures: evFeatures?.addFeaturesFormatted,
      msrpText: msrp?.unknown,
      msrp: msrp?.amount?.value,
      msrpAsShownText: msrpAsShown?.unknown,
      msrpAsShown: msrpAsShown?.amount?.value,
      subHeader: subHeader,
    };
  });

  return children(normalizedOptions);
};

const Models = (props) => {
  const {
    matrix,
    cgiBackgroundColor,
    animate,
    animationCallback,
    cgiSizes,
    disclaimer,
    isSelectionRevealed,
    isBodyTypeRevealed,
    bottomOffset,
    config,
  } = props;
  const animation = { animate, animationCallback };
  const { selections } = config;
  const { bodyTypes } = matrix;
  const hasCabSizes = some(bodyTypes, 'cabSize');
  const getChecked = (option) => {
    return (
      (isBodyTypeRevealed || isSelectionRevealed) &&
      (hasCabSizes
        ? option.cabSize === selections.cabSize
        : option.bodyType === selections.bodyType)
    );
  };
  const additionalOptionsHeights = {};

  const onChange = (option, onTrimChange) => {
    const { bodyType, cabSize } = option;
    const { options } = matrix;
    const { driveType, trimName, engine, transmission } = config.selections;
    const msrp = config.msrp.totalMsrpWithOptions;

    const hasCabSizes = some(options, 'cabSize');
    const bodyFilter = hasCabSizes ? { cabSize } : { bodyType };
    const filteredOptions = filter(options, bodyFilter);

    const selectedOption =
      find(filteredOptions, { driveType, trimName, engine, transmission }) ||
      findClosestMsrp(filter(filteredOptions, { driveType, trimName }), msrp) ||
      findClosestMsrp(filter(filteredOptions, { driveType }), msrp) ||
      findClosestMsrp(filteredOptions, msrp);

    onTrimChange(selectedOption);
  };

  return (
    <ConfigPage step={MODELS}>
      {({ onTrimChange, isUpdating, updatingOption }) => (
        <NormalizeBodyTypeOptions matrix={matrix} cgiSizes={cgiSizes}>
          {(options) => (
            <GridView
              step={MODELS}
              isSelectionRevealed={isBodyTypeRevealed}
              bottomOffset={bottomOffset}
              options={
                <AlignCardsContent numberOfCards={options.length}>
                  {({
                    maxHeight: primaryHeight,
                    getRefByIndex: getPrimaryRefByIndex,
                  }) => (
                    <AlignCardsContent numberOfCards={options.length}>
                      {({
                        maxHeight: secondaryHeight,
                        getRefByIndex: getSecondaryRef,
                      }) => (
                        <>
                          {options.map((option, index) => (
                            <ModelCard
                              key={option.bodyType}
                              background={cgiBackgroundColor}
                              option={option}
                              disclaimer={disclaimer}
                              checked={getChecked(option)}
                              onChange={() => {
                                onChange(option, onTrimChange);
                              }}
                              bodyIndex={index}
                              isUpdating={isUpdating}
                              isUpdatingOption={
                                isUpdating &&
                                get(updatingOption, 'bodyType') ===
                                  option.bodyType
                              }
                              wait={index * 200}
                              animation={animation}
                              additionalOptionsHeights={
                                additionalOptionsHeights
                              }
                              primaryRef={getPrimaryRefByIndex(index)}
                              primaryHeight={primaryHeight}
                              secondaryRef={getSecondaryRef(index)}
                              secondaryHeight={secondaryHeight}
                            />
                          ))}
                        </>
                      )}
                    </AlignCardsContent>
                  )}
                </AlignCardsContent>
              }
              gridClassName="gridview__grid-container--models"></GridView>
          )}
        </NormalizeBodyTypeOptions>
      )}
    </ConfigPage>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { config, matrix, isSelectionRevealed, isBodyTypeRevealed } = state;
  const {
    display: { cgiBackgroundColor },
    cgiSize: cgiSizes,
  } = state.appResources;
  return {
    cgiBackgroundColor,
    config,
    matrix,
    isBodyTypeRevealed,
    isSelectionRevealed,
    cgiSizes,
    ...ownProps,
  };
};

export default connect(mapStateToProps)(Models);
